import { createMachine, transition, type AnyMachineSnapshot } from "xstate";
import { METERS } from "@/data/tunables";
import { deaDanger, heatDanger } from "@/systems/dangerBands";
import type { GameState } from "@/state/GameState";

/**
 * M4 — RunMeta (v0.10 RFC §3): the whole run as one chart.
 *
 *   running ──END_RAID──▶ raid              (dea ≥ deaCeiling; payout ×0.25)
 *   running ──END_SHUTDOWN──▶ shutdown      (heat ≥ heatCeiling; ×0.5)
 *   running ──END_COLLAPSE──▶ collapse      (rep ≤ repFloor; ×0.5)
 *   running ──SELL[offered]──▶ sold          (voluntary; full payout)
 *
 * `running` carries two parallel danger bands (heat calm/hot, dea
 * cold/watched) that flip by PROJECTION — the same meter predicates the
 * engine uses. The endings are TRANSIENT: forcedPrestige/prestige reset
 * the run in the same tick, so the save never stores an ending; the
 * machine arbitrates the exit and the engine performs the reset.
 * Thresholds stay in tunables (read by the projector/callers — never
 * copied into the machine).
 */

export type RunMetaEvent =
  | { type: "END_RAID" }
  | { type: "END_SHUTDOWN" }
  | { type: "END_COLLAPSE" }
  | { type: "SELL"; offered: boolean };

export const runMetaMachine = createMachine({
  types: {} as { events: RunMetaEvent },
  id: "runMeta",
  initial: "running",
  states: {
    running: {
      type: "parallel",
      states: {
        heatBand: {
          initial: "calm",
          states: { calm: {}, hot: {} }, // flips by projection (heatDanger)
        },
        deaBand: {
          initial: "cold",
          states: { cold: {}, watched: {} }, // flips by projection (deaDanger)
        },
      },
      on: {
        END_RAID: { target: "raid", actions: [{ type: "forcePrestige" }] },
        END_SHUTDOWN: { target: "shutdown", actions: [{ type: "forcePrestige" }] },
        END_COLLAPSE: { target: "collapse", actions: [{ type: "forcePrestige" }] },
        SELL: {
          target: "sold",
          guard: ({ event }) => event.type === "SELL" && event.offered,
          actions: [{ type: "bankAndReset" }],
        },
      },
    },
    raid: { type: "final" },
    shutdown: { type: "final" },
    collapse: { type: "final" },
    sold: { type: "final" },
  },
});

/** A live run always projects as `running` + its danger bands (endings
 * are transient — the reset happens in the same tick). */
export function projectRunMeta(state: GameState): AnyMachineSnapshot {
  return runMetaMachine.resolveState({
    value: {
      running: {
        heatBand: heatDanger(state) ? "hot" : "calm",
        deaBand: deaDanger(state) ? "watched" : "cold",
      },
    },
  }) as AnyMachineSnapshot;
}

export type GameOverReason = "raid" | "shutdown" | "collapse";

/**
 * The game-over arbiter (routes meters.checkGameOver): evaluates the
 * ceilings in the RULED priority (raid > shutdown > collapse), fires the
 * matching END event, and reports the ending the machine reached. Pure.
 */
export function fireGameOver(state: GameState): GameOverReason | null {
  let event: RunMetaEvent | null = null;
  if (state.run.dea >= METERS.deaCeiling) event = { type: "END_RAID" };
  else if (state.run.heat >= METERS.heatCeiling) event = { type: "END_SHUTDOWN" };
  else if (state.run.reputation <= METERS.repFloor) event = { type: "END_COLLAPSE" };
  if (event === null) return null;
  const [after, actions] = transition(runMetaMachine, projectRunMeta(state), event);
  const forced = actions.some((a) => (a as { type?: string }).type === "forcePrestige");
  if (!forced) return null; // the machine refused — no ending
  return after.value as GameOverReason;
}

/** The voluntary SELL arbiter (routes prestige()): refused without an
 * open offer — the machine's guard, not a boolean check. Pure. */
export function fireSell(state: GameState): boolean {
  const [after, actions] = transition(runMetaMachine, projectRunMeta(state), {
    type: "SELL",
    offered: state.run.prestigeOffered,
  });
  return after.value === "sold" && actions.some((a) => (a as { type?: string }).type === "bankAndReset");
}
